import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/App";
import { AlertTriangle, ArrowRight, Shield, Target } from "lucide-react";

/**
 * CLARIAS Command Screen
 * Shows ONE message and ONE CTA only
 * No secondary actions, no navigation, no scrolling
 */

const CommandScreenPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [command, setCommand] = useState(location.state?.command || null);
  const [loading, setLoading] = useState(!command);

  useEffect(() => {
    if (!command) {
      fetchCommand();
    }
  }, []);

  const fetchCommand = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/command/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.intelligence_check_completed) {
        navigate("/intelligence-check");
        return;
      }
      
      setCommand(response.data.command);
    } catch (error) {
      console.error("Error fetching command:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = () => {
    if (command?.cta_path) {
      navigate(command.cta_path);
    }
  };

  // Priority-based styling
  const getPriorityStyles = () => {
    switch (command?.priority) {
      case "critical":
        return {
          bg: "from-red-900 via-red-800 to-slate-900",
          accent: "bg-red-500",
          icon: AlertTriangle,
          iconColor: "text-red-400",
          buttonBg: "bg-red-600 hover:bg-red-700",
          glow: "bg-red-600/20"
        };
      case "high":
        return {
          bg: "from-amber-900 via-orange-900 to-slate-900",
          accent: "bg-amber-500",
          icon: AlertTriangle,
          iconColor: "text-amber-400",
          buttonBg: "bg-amber-600 hover:bg-amber-700",
          glow: "bg-amber-600/20"
        };
      case "medium":
        return {
          bg: "from-blue-900 via-indigo-900 to-slate-900",
          accent: "bg-blue-500",
          icon: Target,
          iconColor: "text-blue-400",
          buttonBg: "bg-blue-600 hover:bg-blue-700",
          glow: "bg-blue-600/20"
        };
      default:
        return {
          bg: "from-emerald-900 via-teal-900 to-slate-900",
          accent: "bg-emerald-500",
          icon: Shield,
          iconColor: "text-emerald-400",
          buttonBg: "bg-emerald-600 hover:bg-emerald-700",
          glow: "bg-emerald-600/20"
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Analyzing your preparation...</p>
        </div>
      </div>
    );
  }

  if (!command) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-slate-400 mb-4">Unable to load command</p>
          <Button onClick={() => navigate("/intelligence-check")}>
            Start Intelligence Check
          </Button>
        </div>
      </div>
    );
  }

  const styles = getPriorityStyles();
  const IconComponent = styles.icon;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${styles.bg} flex items-center justify-center p-4`}>
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${styles.glow} rounded-full blur-3xl`} />
      </div>

      <div className="relative w-full max-w-2xl text-center">
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${styles.accent}/20 mb-8`}>
          <IconComponent className={`w-10 h-10 ${styles.iconColor}`} />
        </div>

        {/* Main Message - ONE message only */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
          {command.message}
        </h1>

        {/* Risk indicator */}
        {command.priority === "critical" && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full mb-8">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-400 text-sm font-medium">CRITICAL RISK DETECTED</span>
          </div>
        )}
        {command.priority === "high" && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full mb-8">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            <span className="text-amber-400 text-sm font-medium">HIGH PRIORITY</span>
          </div>
        )}

        {/* Single CTA - ONE action only */}
        <Button
          onClick={handleAction}
          size="lg"
          className={`${styles.buttonBg} text-white text-lg px-8 py-6 rounded-xl shadow-2xl transition-all duration-300 hover:scale-105`}
        >
          {command.cta_text}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>

        {/* No other actions, no navigation */}
        <p className="mt-8 text-slate-500 text-sm">
          One correct decision today beats 10 random tasks.
        </p>
      </div>
    </div>
  );
};

export default CommandScreenPage;
